import { Box, Button, FormControl, FormControlLabel, FormHelperText, InputLabel, LinearProgress, MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@material-ui/core";
import { useStyles } from "../Bookings/Bookings.style";
import { Redirect, useParams } from "react-router";
import { permissionsUpdateThunk, selectAllUsers, selectListPermissionApiStatus, selectPermissions, selectUpdatePermissionApiStatus, selectUserById, usersListThunk } from "store/reducers/temple/usersSlice";
import { UserRoleEnum } from "types/api/temple";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ChevronLeft } from "@material-ui/icons";
import { apiStatus } from "shared/constants/AppEnum";
import React, { useEffect, useState } from "react";
import IntlMessages from "@crema/utility/IntlMessages";
import { Link } from "react-router-dom";
import { areArraysEqual } from "utils/fns";
import { useSnackbar } from "notistack";
import { extractError } from "utils/api";
import { unwrapResult } from "@reduxjs/toolkit";
import { BLACKLISTED_PERMISSIONS } from "utils/permissions";

export const TRANSACTION_VIEW_PERMISSION = 'view_transaction'
export const VIEW_ALL_TRANSACTION_PERMISSION = 'view_all_transaction'

export const getTransactionAllowedUsersPayload = (
    hasViewAllTransaction: boolean,
    selectedUserIds: number[],
) => hasViewAllTransaction ? [] : selectedUserIds

export const getFieldErrorText = (error: any, field: string) => {
    const fieldError = error?.[field]
    if (Array.isArray(fieldError)) return fieldError.join(', ')
    if (typeof fieldError === 'string') return fieldError
    return ''
}

export const getPermissionCategoryLabel = (category: string) =>
    category
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, letter => letter.toUpperCase())

export const getPermissionGroupBackground = (index: number) =>
    index % 2 === 0 ? '#fff' : '#f6faff'

export default function Permissions() {
    const classes = useStyles();
    const { id } = useParams<{ id: string }>()
    const userItem = useAppSelector(state => selectUserById(state, id))
    const api = useAppSelector(selectListPermissionApiStatus)
    const dispatch = useAppDispatch()
    const permissions = useAppSelector(selectPermissions)
    const permissionApi = useAppSelector(selectUpdatePermissionApiStatus)
    const templeUsers = useAppSelector(selectAllUsers)
    const [userPermissions, setPermission] = useState<string[]>([])
    const [transactionAllowedUserIds, setTransactionAllowedUserIds] = useState<number[]>([])
    const [role, setRole] = useState<UserRoleEnum>(UserRoleEnum.staff)
    const { enqueueSnackbar } = useSnackbar()

    const hasViewTransaction = userPermissions.includes(TRANSACTION_VIEW_PERMISSION)
    const hasViewAllTransaction = userPermissions.includes(VIEW_ALL_TRANSACTION_PERMISSION)
    const showTransactionVisibility = role === UserRoleEnum.staff && hasViewTransaction
    const transactionVisibilityError = getFieldErrorText(permissionApi?.error, 'transactionViewAllowedUsers')
    const transactionVisibilityUsers = templeUsers.filter(item => item.temple === userItem?.temple)
    const visiblePermissionCategories = Object.keys(permissions ?? {}).filter(category => !BLACKLISTED_PERMISSIONS.includes(category))

    useEffect(() => {
        dispatch(usersListThunk())
    }, [dispatch])

    useEffect(() => {
        if (role === UserRoleEnum.admin) {
            let newPermissions: string[] = []
            Object.values(permissions).forEach(c => {
                newPermissions = [...newPermissions, ...c.map(p => p[0])]
            })
            setPermission(newPermissions)
        }
    }, [permissions, role])


    useEffect(() => {
        if (userItem?.permissions) setPermission(userItem?.permissions ?? [])
        setTransactionAllowedUserIds(userItem?.transactionViewAllowedUsers ?? [])
        if (userItem?.role) setRole(userItem?.role as UserRoleEnum)
    }, [userItem?.permissions, userItem?.role, userItem?.transactionViewAllowedUsers])

    const handleTogglePermission = (permission: string) => {
        let newPermissions = [...userPermissions]
        if (userPermissions.includes(permission)) {
            newPermissions = userPermissions.filter(p => p !== permission)
        } else {
            newPermissions = [...(userPermissions ?? []), permission]
        }
        setPermission(newPermissions)
    }

    const savePermissions = () => {
        dispatch(permissionsUpdateThunk({
            userId: id,
            data: {
                permissions: userPermissions,
                role,
                transactionViewAllowedUsers: getTransactionAllowedUsersPayload(
                    hasViewAllTransaction,
                    transactionAllowedUserIds,
                )
            }
        })).then(unwrapResult).then(() => {
            enqueueSnackbar("Updated permissions", { variant: 'success' })
        }).catch(err => {
            enqueueSnackbar(extractError(err), { variant: 'error' })
        })
    }

    if (!userItem || !permissions) return <Redirect to='/dashboard/manage-users' />

    return (
        <div className={classes.root}>
            <Paper className={classes.paper} style={{ maxWidth: '1000px' }}>

                <Box p={5} position="sticky" top={-32} bgcolor={'#fff'} zIndex={999}>
                    <Link style={{ textDecoration: 'none', color: 'inherit' }} to="/dashboard/manage-users">
                        <Box display="flex" ml={-1} mb={2}>
                            <ChevronLeft fontSize="small" />
                            <Typography variant="body1">Manage Users</Typography>
                        </Box>
                    </Link>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" id="tableTitle">
                            Permissions for {userItem.user.fullName}
                        </Typography>

                        <Box display="flex" style={{ gap: '20px' }}>
                            <TextField select size="small" variant="outlined" value={role} onChange={(e) => setRole(e.target.value as UserRoleEnum)}>
                                <MenuItem value={UserRoleEnum.staff}>Staff</MenuItem>
                                <MenuItem value={UserRoleEnum.admin}>Admin</MenuItem>
                            </TextField>

                            <Button onClick={savePermissions} disabled={(areArraysEqual(userItem?.permissions, userPermissions) && userItem.role === role && areArraysEqual(userItem?.transactionViewAllowedUsers ?? [], transactionAllowedUserIds)) || permissionApi?.status === apiStatus.LOADING} color="primary" variant="contained">
                                Update
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {showTransactionVisibility && (
                    <Box px={5} pb={3}>
                        <Typography variant="subtitle1">Transaction visibility</Typography>
                        {hasViewAllTransaction ? (
                            <Typography variant="body2" color="textSecondary">
                                This staff user can view all transactions.
                            </Typography>
                        ) : (
                            <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                                error={!!transactionVisibilityError}
                                style={{ marginTop: 12 }}
                            >
                                <InputLabel id="transaction-view-users-label">Allowed users</InputLabel>
                                <Select
                                    labelId="transaction-view-users-label"
                                    multiple
                                    value={transactionAllowedUserIds}
                                    onChange={(event) => setTransactionAllowedUserIds(event.target.value as number[])}
                                    label="Allowed users"
                                    renderValue={(selected) => (selected as number[]).map(userId => {
                                        const user = transactionVisibilityUsers.find(item => item.user.id === userId)
                                        return user?.user.fullName ?? `#${userId}`
                                    }).join(', ')}
                                >
                                    {transactionVisibilityUsers.map(item => (
                                        <MenuItem key={item.user.id} value={item.user.id}>
                                            {item.user.fullName} (#{item.user.id})
                                        </MenuItem>
                                    ))}
                                </Select>
                                {transactionVisibilityError && (
                                    <FormHelperText>{transactionVisibilityError}</FormHelperText>
                                )}
                            </FormControl>
                        )}
                    </Box>
                )}

                <Table size="small" style={{ tableLayout: 'auto', maxWidth: '100%', overflowX: 'auto' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>Permission Name</TableCell>
                            <TableCell align="right">Permission Access</TableCell>
                        </TableRow>
                    </TableHead>
                    {api.status === apiStatus.LOADING && <LinearProgress />}
                    <TableBody>
                        {visiblePermissionCategories.map((category, categoryIndex) =>
                            permissions[category].map((permission, index) => {
                                const backgroundColor = getPermissionGroupBackground(categoryIndex)
                                return (
                                    <TableRow key={`${category}_${permission[0]}-${index}`} style={{ backgroundColor }}>
                                        {index === 0 && (
                                            <TableCell rowSpan={permissions[category].length} style={{ borderRight: "1px solid rgba(224, 224, 224, 1)", backgroundColor }}>
                                                <IntlMessages
                                                    id={`users.permission.${category}`}
                                                    defaultMessage={getPermissionCategoryLabel(category)}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell >{permission[1]}</TableCell>
                                        <TableCell align="right">
                                            <FormControlLabel
                                                label={userPermissions.includes(permission[0]) ? "Allowed" : "Restricted"}
                                                labelPlacement="start"
                                                control={<Switch onChange={() => handleTogglePermission(permission[0])} color="primary" checked={userPermissions.includes(permission[0])} />} disabled={role === UserRoleEnum.admin} />
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </div>
    )
}
